/**
 * ReportFooter — sources and method.
 *
 * The promo moved up into `NotificationsSection`, next to the call to
 * action it belongs to. What stays here is provenance: where the numbers
 * came from and what the report does and does not claim.
 *
 * The disclaimer is not boilerplate. A report that tells someone their
 * account scores 26/100 and has 88 000 ₽ at risk has to say plainly what it
 * is claiming: not a predicted loss, but observed spend that ran while a
 * known error was active. Overstating that would be the fastest way to make
 * the whole document read as a sales pitch.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Divider} from 'ui/components/atoms/Divider';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {AD_SYSTEM_LABEL} from '../content';
import {formatNumber, longDate, shortDate} from '../helpers';
import type {IsoDate, ReportMeta} from '../types';

export interface ReportFooterProps {
  meta: ReportMeta;
  /** Название проекта — оно живёт в `project`, а не в реквизитах сборки. */
  projectName?: string | null;
  /** Дата съёма данных: момент фиксации снимка. */
  dataDate: IsoDate;
  /** Сколько событий истории изменений легло в анализ работы команды. */
  changeLogEvents?: number | null;
  /**
   * Участвовал ли журнал правок в сборке. Пусто — считаем, что участвовал:
   * так собраны все отчёты до появления статуса секции правок.
   */
  changeLogUsed?: boolean;
}

const Fine = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceMuted'),
});

export function ReportFooter({
  meta,
  projectName,
  dataDate,
  changeLogEvents,
  changeLogUsed = true,
}: ReportFooterProps) {
  return (
    <Stack gap='m'>
      <Divider color='surface1' />

      <Stack gap='xs'>
        <Fine>
          Отчёт сформирован по данным кабинета AdSensor (проект «{projectName}»,{' '}
          {AD_SYSTEM_LABEL[meta.adSystem] ?? meta.adSystem})
          {changeLogUsed ? ' и журналу «История изменений»' : ''} за {shortDate(meta.periodStart)}—
          {shortDate(meta.periodEnd)}
          {changeLogUsed && changeLogEvents ? ` — ${formatNumber(changeLogEvents)} событий` : ''}.
          Данные на {longDate(dataDate)}.
        </Fine>
        <Fine>
          Принцип AdSensor: мы не обещаем «точную сумму потерь». Мы показываем наблюдаемый расход
          под риском — сколько денег тратилось, пока ошибка была активна, — и контролируем
          устранение каждого инцидента.
        </Fine>
      </Stack>
    </Stack>
  );
}
